/**
 * UMS – UNITED MACHINERY STORE Cinematic Intro Screen
 * Premium High-Definition Visuals · Interactive Brand Showcase
 * Scenes: Title → Paints → Lubricants → Pipes & Tanks → Pumps & Motors → Bath Fittings → Finale
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';

// ─── CONFIGURATION ────────────────────────────────────────────────────────────
const CONFIG = {
  loginPath: '/login',
  tagline: 'Everything Your Build Needs. One Store.',
  storageKey: 'unibm_intro_seen_v2',
  ttlHours: 24,
  sceneColors: [
    { from: '#040711', to: '#0b1226', accent: '#f97316', label: 'UNITED MACHINERY STORE' },
    { from: '#170603', to: '#2f0d05', accent: '#f59e0b', label: 'PREMIUM PAINTS & FINISHES' },
    { from: '#120801', to: '#261402', accent: '#fbbf24', label: 'AUTOMOTIVE & INDUSTRIAL LUBRICANTS' },
    { from: '#011224', to: '#022442', accent: '#38bdf8', label: 'PIPES, VALVES & WATER STORAGE' },
    { from: '#09031c', to: '#180738', accent: '#818cf8', label: 'PUMPS, MOTORS & TURBINES' },
    { from: '#021317', to: '#042730', accent: '#2dd4bf', label: 'LUXURY BATH & SANITARY FITTINGS' },
    { from: '#040711', to: '#0c1328', accent: '#f97316', label: 'AUTHORIZED QUALITY BRANDS' },
  ],
  brands: {
    paints: [
      { id: 'asianpaints', name: 'Asian Paints', category: 'Paints & Coatings', color: '#e11d48' },
      { id: 'berger',      name: 'Berger Paints', category: 'Decorative Paints', color: '#ea580c' },
    ],
    lubes: [
      { id: 'castrol',     name: 'Castrol',            category: 'Engine Oils', color: '#16a34a' },
      { id: 'servo',       name: 'Servo (Indian Oil)', category: 'Industrial Lubes', color: '#ea580c' },
    ],
    pipes: [
      { id: 'vectus',      name: 'Vectus Tanks',       category: 'Water Storage', color: '#0284c7' },
      { id: 'tata',        name: 'Tata Pipes',         category: 'GI & Steel Pipes', color: '#2563eb' },
      { id: 'kisanshakti', name: 'Kisan Shakti',       category: 'Agricultural Pipes', color: '#059669' },
      { id: 'supreme',     name: 'Supreme',            category: 'CPVC & PVC Pipes', color: '#dc2626' },
    ],
    pumps: [
      { id: 'kirloskar',   name: 'Kirloskar',     category: 'Electric Pumps', color: '#4f46e5' },
      { id: 'crompton',    name: 'Crompton',      category: 'Submersible Motors', color: '#0891b2' },
      { id: 'fieldmarshal',name: 'Field Marshal', category: 'Diesel Engines', color: '#b45309' },
      { id: 'silver',      name: 'Silver',        category: 'Borewell Pumps', color: '#64748b' },
    ],
    bath: [
      { id: 'jaquar',      name: 'Jaquar',  category: 'Luxury Faucets', color: '#0d9488' },
      { id: 'gravity',     name: 'Gravity', category: 'Bath Fittings', color: '#7c3aed' },
    ],
  },
} as const;

type BrandItem = {
  id: string;
  name: string;
  category: string;
  color: string;
};

// ─── STORAGE HELPERS ──────────────────────────────────────────────────────────
function markIntroSeen(): void {
  try {
    localStorage.setItem(CONFIG.storageKey, JSON.stringify({ ts: Date.now() }));
  } catch {
    /* quota fallback */
  }
}

// ─── PROFESSIONAL VECTOR BRAND LOGOS ──────────────────────────────────────────
const BrandLogo: React.FC<{ id: string; color: string }> = ({ id }) => {
  switch (id) {
    case 'asianpaints':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <path d="M6 26C8 18 14 10 26 6C22 14 18 20 6 26Z" fill="#e11d48" />
          <path d="M12 26C15 20 19 15 27 10C24 18 19 23 12 26Z" fill="#f59e0b" opacity="0.9" />
          <circle cx="23" cy="8" r="3.2" fill="#ec4899" />
        </svg>
      );
    case 'berger':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <path d="M16 3L27 7V16C27 22.5 22.3 28.5 16 30C9.7 28.5 5 22.5 5 16V7L16 3Z" fill="#1e3a8a" stroke="#f59e0b" strokeWidth="1.5" />
          <path d="M16 8L13 14H19L16 8Z" fill="#f59e0b" />
          <path d="M11 17C11 17 13 22 16 22C19 22 21 17 21 17" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'castrol':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" stroke="#16a34a" strokeWidth="2.5" />
          <path d="M7 21C11 13 21 10 25 11" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
          <path d="M9 16C12 12 18 11 23 13" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="16" cy="16" r="3.5" fill="#16a34a" />
        </svg>
      );
    case 'servo':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#ea580c" />
          <circle cx="16" cy="16" r="10" stroke="#fef08a" strokeWidth="1.8" strokeDasharray="3 2" />
          <circle cx="16" cy="16" r="5.5" fill="#facc15" />
          <path d="M16 5V9M16 23V27M5 16H9M23 16H27" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'vectus':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <rect x="7" y="7" width="18" height="20" rx="3" fill="#0369a1" stroke="#38bdf8" strokeWidth="1.5" />
          <line x1="7" y1="12" x2="25" y2="12" stroke="#38bdf8" strokeWidth="1.5" />
          <line x1="7" y1="17" x2="25" y2="17" stroke="#38bdf8" strokeWidth="1.5" />
          <line x1="7" y1="22" x2="25" y2="22" stroke="#38bdf8" strokeWidth="1.5" />
          <path d="M11 7V5C11 4 12 3 13 3H19C20 3 21 4 21 5V7" stroke="#38bdf8" strokeWidth="1.5" />
        </svg>
      );
    case 'tata':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#1d4ed8" stroke="#60a5fa" strokeWidth="1.5" />
          <path d="M9 11H23M16 11V23" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
        </svg>
      );
    case 'kisanshakti':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#047857" stroke="#34d399" strokeWidth="1.5" />
          <path d="M16 7C16 7 10 13 10 18C10 21.3 12.7 24 16 24C19.3 24 22 21.3 22 18C22 13 16 7 16 7Z" fill="#34d399" />
          <path d="M16 13V21M13 17L16 14L19 17" stroke="#064e3b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'supreme':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <path d="M16 4L28 16L16 28L4 16L16 4Z" fill="#b91c1c" stroke="#f87171" strokeWidth="1.5" />
          <rect x="12" y="12" width="8" height="8" rx="1.5" fill="#ffffff" />
        </svg>
      );
    case 'kirloskar':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#312e81" stroke="#818cf8" strokeWidth="1.5" />
          <path d="M16 7L18.5 12.5H24L19.5 16L21.5 21.5L16 18L10.5 21.5L12.5 16L8 12.5H13.5L16 7Z" fill="#fbbf24" />
        </svg>
      );
    case 'crompton':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#0e7490" stroke="#22d3ee" strokeWidth="1.5" />
          <path d="M16 6C21.5 6 26 10.5 26 16C26 21.5 21.5 26 16 26" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="16" cy="16" r="4" fill="#22d3ee" />
          <path d="M14 12L18 16L14 20" stroke="#083344" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'fieldmarshal':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#78350f" stroke="#f59e0b" strokeWidth="1.5" />
          <circle cx="16" cy="16" r="7" stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="4 2" />
          <circle cx="16" cy="16" r="3" fill="#fbbf24" />
        </svg>
      );
    case 'silver':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#334155" stroke="#94a3b8" strokeWidth="1.5" />
          <path d="M10 22C10 22 12 11 16 11C20 11 22 22 22 22" stroke="#e2e8f0" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="16" cy="11" r="3" fill="#38bdf8" />
        </svg>
      );
    case 'jaquar':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#0f766e" stroke="#2dd4bf" strokeWidth="1.5" />
          <path d="M9 13C9 9 14 8 18 8H23V13C23 15 21 16 19 16H15" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15 16V22" stroke="#2dd4bf" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="15" cy="24" r="1.5" fill="#ffffff" />
        </svg>
      );
    case 'gravity':
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#5b21b6" stroke="#a78bfa" strokeWidth="1.5" />
          <path d="M16 6V26M10 20L16 26L22 20" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="16" cy="10" r="2" fill="#c4b5fd" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
          <circle cx="16" cy="16" r="13" fill="#1e293b" stroke="#64748b" strokeWidth="1.5" />
          <circle cx="16" cy="16" r="4" fill="#94a3b8" />
        </svg>
      );
  }
};

// ─── PREMIUM GLASSMORPHIC BRAND CARD ──────────────────────────────────────────
const BrandCard: React.FC<{ brand: BrandItem; accent: string; index: number; compact?: boolean }> = ({
  brand,
  accent,
  index,
  compact = false,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [imgErr, setImgErr] = useState(false);
  const logoUrl = `/assets/brands/${brand.id}.png`;

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const rx = (e.clientX - r.left) / r.width - 0.5;
    const ry = (e.clientY - r.top) / r.height - 0.5;
    gsap.to(el, { rotateY: rx * 12, rotateX: -ry * 12, scale: 1.04, duration: 0.25, ease: 'power2.out' });
  };

  const onMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    gsap.to(el, { rotateY: 0, rotateX: 0, scale: 1, duration: 0.5, ease: 'elastic.out(1,0.6)' });
  };

  useEffect(() => {
    if (!cardRef.current) return;
    if (window.matchMedia('(pointer: coarse)').matches) {
      gsap.to(cardRef.current, {
        rotateY: 4,
        rotateX: -3,
        duration: 2.8 + index * 0.25,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }, [index]);

  return (
    <div
      ref={cardRef}
      className={`unibm-brand-card ${compact ? 'unibm-card-compact' : ''}`}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{
        opacity: 0,
        transform: 'translateY(24px)',
        filter: 'blur(8px)',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1.5px solid ${accent}45`,
        borderRadius: compact ? 10 : 14,
        padding: compact ? '6px 12px' : '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 12,
        cursor: 'default',
        transformStyle: 'preserve-3d',
        willChange: 'transform',
        boxShadow: `0 6px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.14), 0 0 16px ${accent}18`,
        minWidth: compact ? 130 : 160,
        maxWidth: compact ? 180 : 225,
        flex: compact ? '1 1 140px' : undefined,
        position: 'relative',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      <div
        style={{
          width: compact ? 36 : 48,
          height: compact ? 36 : 48,
          flexShrink: 0,
          borderRadius: compact ? 8 : 10,
          background: '#ffffff',
          border: '1px solid rgba(255,255,255,0.35)',
          boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: compact ? 3 : 4,
        }}
      >
        {!imgErr ? (
          <img
            src={logoUrl}
            alt={brand.name}
            onError={() => setImgErr(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <BrandLogo id={brand.id} color={brand.color} />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span
          style={{
            color: '#ffffff',
            fontSize: compact ? 11.5 : 12.5,
            fontWeight: 700,
            letterSpacing: '0.02em',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textShadow: '0 1px 4px rgba(0,0,0,0.6)',
          }}
        >
          {brand.name}
        </span>
        <span
          style={{
            color: `${accent}ee`,
            fontSize: compact ? 8.5 : 9.5,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            lineHeight: 1.2,
          }}
        >
          {brand.category}
        </span>
      </div>

      {/* Subtle bottom edge glow */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 10,
          right: 10,
          height: 2,
          borderRadius: 1,
          background: `linear-gradient(90deg, transparent, ${accent}aa, transparent)`,
        }}
      />
    </div>
  );
};

// ─── SKIP BUTTON ──────────────────────────────────────────────────────────────
const SkipButton: React.FC<{ onSkip: () => void; accent: string }> = ({ onSkip, accent }) => {
  return (
    <button
      id="unibm-skip-btn"
      onClick={onSkip}
      tabIndex={0}
      aria-label="Skip intro and go to login"
      style={{
        position: 'fixed',
        bottom: 36,
        right: 28,
        zIndex: 100020,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 22px',
        background: 'rgba(5, 8, 18, 0.72)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1.5px solid ${accent}66`,
        borderRadius: 999,
        cursor: 'pointer',
        color: '#ffffff',
        fontSize: 12,
        fontWeight: 800,
        fontFamily: 'Inter, system-ui, sans-serif',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        outline: 'none',
        transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        boxShadow: `0 4px 24px rgba(0,0,0,0.6), 0 0 16px ${accent}25`,
      }}
      onMouseEnter={e => {
        const b = e.currentTarget;
        b.style.borderColor = accent;
        b.style.background = 'rgba(255, 255, 255, 0.14)';
        b.style.transform = 'translateY(-2px) scale(1.02)';
        b.style.boxShadow = `0 8px 28px rgba(0,0,0,0.7), 0 0 25px ${accent}66`;
      }}
      onMouseLeave={e => {
        const b = e.currentTarget;
        b.style.borderColor = `${accent}66`;
        b.style.background = 'rgba(5, 8, 18, 0.72)';
        b.style.transform = 'translateY(0) scale(1)';
        b.style.boxShadow = `0 4px 24px rgba(0,0,0,0.6), 0 0 16px ${accent}25`;
      }}
    >
      <span>Skip</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.9 }}>
        <path d="M5 4l10 8-10 8V4zm11 0h3v16h-3V4z" />
      </svg>
    </button>
  );
};

// ─── TYPEWRITER HOOK ──────────────────────────────────────────────────────────
function useTypewriter(text: string, startDelay = 0, speed = 50) {
  const [shown, setShown] = useState('');
  useEffect(() => {
    setShown('');
    let i = 0;
    const t = setTimeout(() => {
      const iv = setInterval(() => {
        i++;
        setShown(text.slice(0, i));
        if (i >= text.length) clearInterval(iv);
      }, speed);
      return () => clearInterval(iv);
    }, startDelay);
    return () => clearTimeout(t);
  }, [text, startDelay, speed]);
  return shown;
}

// ─── SCENE BADGE HEADER ───────────────────────────────────────────────────────
const SLabel: React.FC<{ category: string; subtitle: string; accent: string }> = ({ category, subtitle, accent }) => (
  <div className="unibm-slabel" style={{ position: 'absolute', top: '10%', left: 0, right: 0, textAlign: 'center', pointerEvents: 'none', zIndex: 15, padding: '0 16px' }}>
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 14px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)',
        border: `1px solid ${accent}40`,
        color: accent,
        fontSize: 10.5,
        letterSpacing: '0.18em',
        fontWeight: 800,
        textTransform: 'uppercase',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, boxShadow: `0 0 8px ${accent}` }} />
      {category}
    </div>
    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: 500, marginTop: 6, letterSpacing: '0.04em' }}>
      {subtitle}
    </div>
  </div>
);

// ─── BRAND ROW ────────────────────────────────────────────────────────────────
const BrandRow: React.FC<{ brands: readonly BrandItem[]; accent: string }> = ({ brands, accent }) => (
  <div
    style={{
      position: 'absolute',
      bottom: 'clamp(20px, 5.5vh, 48px)',
      left: 0,
      right: 0,
      display: 'flex',
      gap: 12,
      flexWrap: 'wrap',
      justifyContent: 'center',
      padding: '0 20px',
      zIndex: 16,
    }}
  >
    {brands.map((b, i) => (
      <BrandCard key={b.id} brand={b} accent={accent} index={i} />
    ))}
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const IntroScreen: React.FC = () => {
  const navigate = useNavigate();

  // DOM refs
  const wrapRef   = useRef<HTMLDivElement>(null);
  const tlRef     = useRef<gsap.core.Timeline | null>(null);
  const doneRef   = useRef(false);

  // Scene panel refs
  const s0 = useRef<HTMLDivElement>(null);
  const s1 = useRef<HTMLDivElement>(null);
  const s2 = useRef<HTMLDivElement>(null);
  const s3 = useRef<HTMLDivElement>(null);
  const s4 = useRef<HTMLDivElement>(null);
  const s5 = useRef<HTMLDivElement>(null);
  const s6 = useRef<HTMLDivElement>(null);

  // Scene animated element refs
  const flareRef       = useRef<HTMLDivElement>(null);
  const logoRef        = useRef<HTMLDivElement>(null);
  const paintBucketRef = useRef<HTMLDivElement>(null);
  const oilStream      = useRef<HTMLDivElement>(null);
  const oilDrop    = useRef<HTMLDivElement>(null);
  const pipeSvgRef = useRef<SVGSVGElement>(null);
  const turbineRef = useRef<HTMLDivElement>(null);
  const waterFlow  = useRef<HTMLDivElement>(null);

  // State
  const [scene, setScene] = useState(0);
  const tagline = useTypewriter(CONFIG.tagline, 600, 45);

  // Navigation: ONLY when clicking "Enter Store" or "Skip"
  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    tlRef.current?.kill();
    markIntroSeen();
    gsap.to(wrapRef.current, {
      opacity: 0,
      scale: 1.04,
      duration: 0.6,
      ease: 'power2.in',
      onComplete: () => navigate(CONFIG.loginPath, { replace: true }),
    });
  }, [navigate]);

  // Escape key also skips
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [finish]);

  // ── GSAP MASTER TIMELINE ──────────────────────────────────────────────────
  useEffect(() => {
    const panels = [s0, s1, s2, s3, s4, s5, s6].map(r => r.current!);
    gsap.set(panels, { opacity: 0, display: 'none' });
    gsap.set(s0.current, { display: 'flex', opacity: 1 });

    // IMPORTANT: No onComplete auto-redirect! The timeline settles on Scene 6.
    const tl = gsap.timeline();
    tlRef.current = tl;

    // Crossfade helper
    const xfade = (from: HTMLDivElement | null, to: HTMLDivElement | null, idx: number) => {
      tl.add(() => setScene(idx));
      if (from) {
        tl.to(from, { opacity: 0, scale: 1.04, duration: 0.5, ease: 'power2.in' });
        tl.set(from, { display: 'none' });
      }
      if (to) {
        tl.set(to, { display: 'flex', opacity: 0, scale: 1 });
        tl.to(to, { opacity: 1, duration: 0.55, ease: 'power1.out' });
      }
    };

    // Stagger brand cards
    const revealCards = (el: HTMLDivElement | null) => {
      const c = el?.querySelectorAll<HTMLElement>('.unibm-brand-card');
      if (!c?.length) return;
      tl.to(Array.from(c), { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.45, stagger: 0.1, ease: 'back.out(1.4)' }, '-=0.15');
    };

    // ── Scene 0: Logo & Title (0–2s)
    gsap.set(flareRef.current, { opacity: 0, x: '-120%' });
    gsap.set(logoRef.current, { opacity: 0, scale: 0.8, filter: 'blur(12px)' });
    tl.to(flareRef.current, { x: '130%', opacity: 0.9, duration: 1.1, ease: 'power2.inOut' });
    tl.to(logoRef.current, { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.7, ease: 'back.out(1.3)' }, '-=0.4');
    tl.to(flareRef.current, { opacity: 0, duration: 0.3 }, '-=0.2');
    tl.to(s0.current, { scale: 1.05, duration: 1.6, ease: 'none' }, '-=0.4');

    // ── Scene 1: Paints (2–5s)
    xfade(s0.current, s1.current, 1);
    gsap.set(paintBucketRef.current, { scale: 0.82, opacity: 0, y: 25 });
    tl.to(paintBucketRef.current, { scale: 1, opacity: 1, y: 0, duration: 0.85, ease: 'back.out(1.2)' }, '-=0.2');
    tl.to(paintBucketRef.current, { scale: 1.05, duration: 2.2, ease: 'none' }, '<');
    revealCards(s1.current);
    tl.to({}, { duration: 1.5 });

    // ── Scene 2: Lubricants (5–7s)
    xfade(s1.current, s2.current, 2);
    gsap.set(oilStream.current, { scaleY: 0, transformOrigin: 'top center' });
    tl.to(oilStream.current, { scaleY: 1, duration: 0.9, ease: 'power2.in' }, '-=0.2');
    gsap.set(oilDrop.current, { scale: 0.2, opacity: 0 });
    tl.to(oilDrop.current, { scale: 2.2, opacity: 0.6, duration: 0.6, ease: 'power2.out' }, '-=0.2');
    revealCards(s2.current);
    tl.to({}, { duration: 1.2 });

    // ── Scene 3: Pipes & Tanks (7–9s)
    xfade(s2.current, s3.current, 3);
    const pipePaths = pipeSvgRef.current ? Array.from(pipeSvgRef.current.querySelectorAll<SVGGeometryElement>('path,line')) : [];
    pipePaths.forEach(p => {
      const len = p.getTotalLength?.() ?? 250;
      gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
    });
    if (pipePaths.length) {
      tl.to(pipePaths, { strokeDashoffset: 0, duration: 0.85, stagger: 0.08, ease: 'power2.inOut' }, '-=0.2');
    }
    revealCards(s3.current);
    tl.to({}, { duration: 1.2 });

    // ── Scene 4: Pumps & Motors (9–11s)
    xfade(s3.current, s4.current, 4);
    gsap.set(turbineRef.current, { scale: 0.6, opacity: 0 });
    tl.to(turbineRef.current, { scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(1.3)' }, '-=0.2');
    revealCards(s4.current);
    tl.to({}, { duration: 1.2 });

    // ── Scene 5: Bath Fittings (11–13s)
    xfade(s4.current, s5.current, 5);
    gsap.set(waterFlow.current, { opacity: 0, scaleY: 0, transformOrigin: 'top center' });
    tl.to(waterFlow.current, { opacity: 0.85, scaleY: 1, duration: 0.8, ease: 'power2.out' }, '-=0.2');
    revealCards(s5.current);
    tl.to({}, { duration: 1.2 });

    // ── Scene 6: Finale Showcase (13s+) - STAYS HERE INTERACTIVELY
    xfade(s5.current, s6.current, 6);
    revealCards(s6.current);

    return () => {
      tl.kill();
      tlRef.current = null;
    };
  }, []);

  const col = CONFIG.sceneColors[scene];
  const allBrands: BrandItem[] = [
    ...CONFIG.brands.paints,
    ...CONFIG.brands.lubes,
    ...CONFIG.brands.pipes,
    ...CONFIG.brands.pumps,
    ...CONFIG.brands.bath,
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
        *::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
        html, body { overflow: hidden !important; width: 100vw !important; height: 100vh !important; margin: 0 !important; }
        @keyframes u_spin { to { transform: rotate(360deg); } }
        @keyframes u_rev_spin { to { transform: rotate(-360deg); } }
        @keyframes u_pulse { 0%,100%{opacity:.5;transform:scale(1)} 50%{opacity:.9;transform:scale(1.05)} }
        @keyframes u_flow { 0%{stroke-dashoffset:40} 100%{stroke-dashoffset:0} }
        @keyframes u_ripple { 0%{transform:translate(-50%,-50%) scale(0.6);opacity:0.8} 100%{transform:translate(-50%,-50%) scale(3.5);opacity:0} }
        @keyframes u_cta_glow { 0%,100%{box-shadow:0 0 24px #f9731666, 0 4px 16px rgba(0,0,0,0.5)} 50%{box-shadow:0 0 48px #f97316cc, 0 6px 24px rgba(0,0,0,0.6)} }
        @keyframes u_float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .unibm-scene { position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;overflow:hidden;will-change:transform,opacity; }
        #unibm-skip-btn:active { transform: scale(0.96) !important; }
        @media(max-width:767px){
          .unibm-lbx { display: none !important; }
          .unibm-brand-card { min-width: 130px !important; max-width: 48% !important; padding: 7px 10px !important; }
          .unibm-card-compact { min-width: 105px !important; max-width: 46% !important; padding: 5px 8px !important; }
          #unibm-skip-btn { top: 14px !important; bottom: auto !important; right: 16px !important; padding: 6px 13px !important; font-size: 11px !important; z-index: 40 !important; }
          .unibm-top-watermark { top: 16px !important; left: 16px !important; font-size: 10px !important; letter-spacing: 0.16em !important; }
          .unibm-slabel { top: clamp(52px, 8vh, 68px) !important; }
          .unibm-scene-finale { padding-top: 48px !important; }
        }
      `}</style>

      {/* FULLSCREEN WRAPPER */}
      <div
        ref={wrapRef}
        role="dialog"
        aria-modal="true"
        aria-label="UMS intro"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99998,
          fontFamily: 'Inter, system-ui, sans-serif',
          background: `linear-gradient(155deg, ${col.from} 0%, ${col.to} 100%)`,
          transition: 'background 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
          overflow: 'hidden',
        }}
      >
        {/* Soft Vignette Overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            pointerEvents: 'none',
            background: 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.85) 100%)',
          }}
        />

        {/* Cinematic Letterbox Bars */}
        <div className="unibm-lbx" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '7%', background: '#000', zIndex: 30 }} />
        <div className="unibm-lbx" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '7%', background: '#000', zIndex: 30 }} />

        {/* Ambient Lens Flare */}
        <div
          ref={flareRef}
          style={{
            position: 'absolute',
            top: '32%',
            left: 0,
            width: '55%',
            height: 3,
            background: `linear-gradient(90deg, transparent, ${col.accent}cc, #ffffff, ${col.accent}cc, transparent)`,
            boxShadow: `0 0 70px 35px ${col.accent}44`,
            zIndex: 25,
            pointerEvents: 'none',
          }}
        />

        {/* Top Watermark - only visible during product category scenes (1–5) */}
        <div
          className="unibm-top-watermark"
          style={{
            position: 'absolute',
            top: '8.5%',
            left: 28,
            color: 'rgba(255,255,255,0.4)',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            zIndex: 32,
            pointerEvents: 'none',
            opacity: scene >= 1 && scene <= 5 ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        >
          UMS · HARDWARE STORE
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SCENE 0: UMS LOGO & TAGLINE
        ════════════════════════════════════════════════════════════════ */}
        <div ref={s0} className="unibm-scene" style={{ zIndex: 10 }}>
          <div
            style={{
              position: 'absolute',
              width: 500,
              height: 500,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${CONFIG.sceneColors[0].accent}22 0%, transparent 70%)`,
              animation: 'u_pulse 3s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />

          <div ref={logoRef} style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <div
              style={{
                fontSize: 'clamp(3.6rem, 12vw, 7.5rem)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 1,
                color: '#ffffff',
                textShadow: `0 0 60px ${CONFIG.sceneColors[0].accent}66, 0 10px 30px rgba(0,0,0,0.8)`,
              }}
            >
              U<span style={{ color: CONFIG.sceneColors[0].accent }}>MS</span>
            </div>
            <div
              style={{
                fontSize: 'clamp(0.72rem, 2vw, 0.98rem)',
                color: 'rgba(255,255,255,0.85)',
                letterSpacing: '0.26em',
                textTransform: 'uppercase',
                marginTop: 14,
                fontWeight: 800,
              }}
            >
              UNITED MACHINERY STORE
            </div>
          </div>

          <div
            style={{
              marginTop: 24,
              color: 'rgba(255,255,255,0.7)',
              fontSize: 'clamp(0.85rem, 2.5vw, 1.15rem)',
              fontWeight: 500,
              textAlign: 'center',
              maxWidth: 480,
              minHeight: '1.4em',
              position: 'relative',
              zIndex: 2,
              padding: '0 16px',
            }}
          >
            {tagline}
            <span style={{ opacity: tagline.length < CONFIG.tagline.length ? 1 : 0, color: CONFIG.sceneColors[0].accent }}>|</span>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SCENE 1: PAINTS & COATINGS
        ════════════════════════════════════════════════════════════════ */}
        <div ref={s1} className="unibm-scene" style={{ zIndex: 10, justifyContent: 'center' }}>
          <SLabel category="Paints & Coatings" subtitle="Vibrant Shades, Durable Protection & Luxury Finishes" accent={CONFIG.sceneColors[1].accent} />

          {/* Centerpiece: Expansive Asian Paints & Berger Paint Buckets with Dynamic Splash Wave */}
          <div
            ref={paintBucketRef}
            style={{
              position: 'relative',
              width: 'min(94vw, 920px)',
              maxHeight: '56vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 'auto',
              filter: 'drop-shadow(0 25px 60px rgba(0,0,0,0.9)) drop-shadow(0 0 50px rgba(245,158,11,0.25))',
            }}
          >
            {/* Dual Ambient Color Glow (Warm Red/Gold on Left, Vibrant Blue/Cyan on Right) */}
            <div
              style={{
                position: 'absolute',
                top: '10%',
                left: '2%',
                width: '52%',
                height: '80%',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(225,29,72,0.45) 0%, rgba(245,158,11,0.3) 50%, transparent 80%)',
                filter: 'blur(50px)',
                animation: 'u_pulse 2.8s ease-in-out infinite',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '10%',
                right: '2%',
                width: '52%',
                height: '80%',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(37,99,235,0.45) 0%, rgba(14,165,233,0.3) 50%, transparent 80%)',
                filter: 'blur(50px)',
                animation: 'u_pulse 2.8s ease-in-out 0.8s infinite',
                pointerEvents: 'none',
              }}
            />

            {/* Official Transparent PNG: Asian Paints & Berger Paint Splash */}
            <img
              src="/images/paint_bucket_flow.png"
              alt="Asian Paints and Berger Paint Buckets with Splash Wave"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '54vh',
                objectFit: 'contain',
                display: 'block',
                animation: 'u_float 3.5s ease-in-out infinite',
                willChange: 'transform',
              }}
            />
          </div>

          <BrandRow brands={CONFIG.brands.paints} accent={CONFIG.sceneColors[1].accent} />
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SCENE 2: LUBRICANTS & OILS
        ════════════════════════════════════════════════════════════════ */}
        <div ref={s2} className="unibm-scene" style={{ zIndex: 10 }}>
          <SLabel category="Lubricants & Engine Oils" subtitle="High-Performance Friction Defense & Machinery Life" accent={CONFIG.sceneColors[2].accent} />

          {/* Center Graphic: Viscous Golden Oil Droplet & Molecular Shockwaves */}
          <div style={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Stream */}
            <div
              ref={oilStream}
              style={{
                position: 'absolute',
                top: 0,
                width: 14,
                height: 110,
                background: 'linear-gradient(180deg, #f59e0b 0%, #d97706 70%, #b45309 100%)',
                borderRadius: '0 0 7px 7px',
                boxShadow: '0 0 24px rgba(245,158,11,0.6)',
                zIndex: 10,
              }}
            />
            {/* Expanding Impact Rings */}
            {[1, 2, 3].map(i => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: '65%',
                  left: '50%',
                  width: 70,
                  height: 30,
                  border: `2px solid ${CONFIG.sceneColors[2].accent}`,
                  borderRadius: '50%',
                  transform: 'translate(-50%,-50%)',
                  animation: `u_ripple ${1.3 + i * 0.4}s ease-out ${i * 0.35}s infinite`,
                  pointerEvents: 'none',
                }}
              />
            ))}
            {/* Glowing Golden Droplet */}
            <div
              ref={oilDrop}
              style={{
                position: 'absolute',
                top: '55%',
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'radial-gradient(circle, #fde047 10%, #d97706 70%, #78350f 100%)',
                boxShadow: '0 0 32px #f59e0b',
                zIndex: 12,
              }}
            />
          </div>

          <BrandRow brands={CONFIG.brands.lubes} accent={CONFIG.sceneColors[2].accent} />
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SCENE 3: PIPES & WATER STORAGE
        ════════════════════════════════════════════════════════════════ */}
        <div ref={s3} className="unibm-scene" style={{ zIndex: 10 }}>
          <SLabel category="Pipes, Valves & Storage" subtitle="High-Pressure Commercial & Domestic Plumbing Solutions" accent={CONFIG.sceneColors[3].accent} />

          {/* Architectural Blueprint Pipe Network SVG */}
          <div style={{ position: 'relative', width: '90%', maxWidth: 740, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg ref={pipeSvgRef} viewBox="0 0 720 180" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
              {/* Main Distribution Line */}
              <path d="M 40 90 H 680" stroke={CONFIG.sceneColors[3].accent} strokeWidth="6" strokeLinecap="round" />
              {/* Branch Downcomers */}
              <line x1="160" y1="90" x2="160" y2="160" stroke={CONFIG.sceneColors[3].accent} strokeWidth="4" />
              <line x1="360" y1="90" x2="360" y2="170" stroke={CONFIG.sceneColors[3].accent} strokeWidth="4" />
              <line x1="560" y1="90" x2="560" y2="160" stroke={CONFIG.sceneColors[3].accent} strokeWidth="4" />
              {/* Flange Couplings */}
              {[160, 360, 560].map(x => (
                <g key={x}>
                  <circle cx={x} cy={90} r={10} fill="#0284c7" stroke="#ffffff" strokeWidth="2" />
                  <circle cx={x} cy={90} r={4} fill="#ffffff" />
                </g>
              ))}
              {/* High-Capacity Water Tank Outline */}
              <rect x="630" y="30" width="70" height="110" rx="8" fill="rgba(2,132,199,0.18)" stroke={CONFIG.sceneColors[3].accent} strokeWidth="3" />
              <line x1="630" y1="65" x2="700" y2="65" stroke={CONFIG.sceneColors[3].accent} strokeWidth="1.5" strokeDasharray="4 3" />
              <line x1="630" y1="105" x2="700" y2="105" stroke={CONFIG.sceneColors[3].accent} strokeWidth="1.5" strokeDasharray="4 3" />
            </svg>
          </div>

          <BrandRow brands={CONFIG.brands.pipes} accent={CONFIG.sceneColors[3].accent} />
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SCENE 4: PUMPS & MOTORS
        ════════════════════════════════════════════════════════════════ */}
        <div ref={s4} className="unibm-scene" style={{ zIndex: 10 }}>
          <SLabel category="Pumps & Electric Motors" subtitle="Submersible, Monoblock & Agricultural Power Equipment" accent={CONFIG.sceneColors[4].accent} />

          {/* Center Graphic: High-Speed Centrifugal Turbine */}
          <div ref={turbineRef} style={{ position: 'relative', width: 170, height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Outer Electromagnetic Stator */}
            <svg viewBox="0 0 100 100" width="160" height="160" style={{ position: 'absolute', animation: 'u_spin 3s linear infinite' }}>
              <circle cx="50" cy="50" r="44" stroke={CONFIG.sceneColors[4].accent} strokeWidth="2" strokeDasharray="8 6" opacity="0.75" />
              {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => (
                <rect key={deg} x="47" y="4" width="6" height="12" rx="2" fill={CONFIG.sceneColors[4].accent} transform={`rotate(${deg} 50 50)`} />
              ))}
            </svg>

            {/* Inner Counter-Rotating Impeller */}
            <svg viewBox="0 0 100 100" width="110" height="110" style={{ position: 'relative', zIndex: 2, animation: 'u_rev_spin 1.8s linear infinite' }}>
              <circle cx="50" cy="50" r="30" fill="rgba(79,70,229,0.25)" stroke="#ffffff" strokeWidth="2" />
              {[0, 60, 120, 180, 240, 300].map(deg => (
                <path key={deg} d="M50 50 Q62 35 68 24" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" transform={`rotate(${deg} 50 50)`} />
              ))}
              <circle cx="50" cy="50" r="9" fill={CONFIG.sceneColors[4].accent} stroke="#ffffff" strokeWidth="2" />
            </svg>
          </div>

          <BrandRow brands={CONFIG.brands.pumps} accent={CONFIG.sceneColors[4].accent} />
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SCENE 5: BATH FITTINGS
        ════════════════════════════════════════════════════════════════ */}
        <div ref={s5} className="unibm-scene" style={{ zIndex: 10 }}>
          <SLabel category="Luxury Bath & Sanitary" subtitle="Architectural Chrome Fixtures & Precision Waterflow" accent={CONFIG.sceneColors[5].accent} />

          {/* Designer Chrome Showerhead & Crystalline Waterfall */}
          <div style={{ position: 'relative', width: 220, height: 180, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Luxury Showerhead Disc */}
            <svg viewBox="0 0 140 40" width="140" height="40" fill="none">
              <path d="M70 4V16M70 16H20C14 16 10 20 10 26V30H130V26C130 20 126 16 120 16H70" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
              <rect x="18" y="30" width="104" height="6" rx="3" fill="#2dd4bf" />
            </svg>

            {/* Cascading Water Streams */}
            <div
              ref={waterFlow}
              style={{
                width: 100,
                height: 110,
                background: 'linear-gradient(180deg, rgba(45,212,191,0.5) 0%, rgba(45,212,191,0.1) 80%, transparent 100%)',
                borderRadius: '0 0 20px 20px',
                display: 'flex',
                justifyContent: 'space-around',
                padding: '0 8px',
                boxShadow: '0 10px 30px rgba(45,212,191,0.3)',
              }}
            >
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} style={{ width: 2, height: '100%', background: 'linear-gradient(180deg, #ffffff 0%, rgba(45,212,191,0.6) 100%)', opacity: 0.8 }} />
              ))}
            </div>
          </div>

          <BrandRow brands={CONFIG.brands.bath} accent={CONFIG.sceneColors[5].accent} />
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SCENE 6: FINALE SHOWCASE - STAYS HERE INTERACTIVELY
        ════════════════════════════════════════════════════════════════ */}
        <div ref={s6} className="unibm-scene unibm-scene-finale" style={{ zIndex: 10, gap: 'clamp(8px, 1.8vh, 18px)', padding: '20px 16px', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: 'clamp(2.4rem, 7vw, 4.8rem)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                color: '#ffffff',
                lineHeight: 1,
                textShadow: `0 0 50px ${CONFIG.sceneColors[6].accent}88`,
              }}
            >
              U<span style={{ color: CONFIG.sceneColors[6].accent }}>MS</span>
            </div>
            <div
              style={{
                fontSize: 'clamp(0.65rem, 1.6vw, 0.85rem)',
                color: 'rgba(255,255,255,0.85)',
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                fontWeight: 800,
                marginTop: 6,
              }}
            >
              UNITED MACHINERY STORE
            </div>
          </div>

          <p
            style={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: 'clamp(0.72rem, 1.6vw, 0.92rem)',
              fontWeight: 500,
              textAlign: 'center',
              maxWidth: 540,
              lineHeight: 1.4,
              padding: '0 16px',
              margin: 0,
            }}
          >
            Authorized Quality Brands. Trusted by Builders, Farmers &amp; Homeowners.
          </p>

          {/* Grid of all 14 Brands - Cleanly fitted without scrollbars */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'clamp(6px, 1vw, 10px)',
              justifyContent: 'center',
              maxWidth: 960,
              width: '100%',
              padding: '4px 10px',
              overflow: 'hidden',
            }}
          >
            {allBrands.map((b, i) => (
              <BrandCard key={b.id} brand={b} accent={b.color} index={i} compact={true} />
            ))}
          </div>

          {/* Action Button: ONLY clicking this (or Skip) opens the login page */}
          <button
            id="unibm-enter-store-btn"
            onClick={finish}
            style={{
              padding: 'clamp(10px, 1.6vh, 14px) clamp(28px, 4vw, 42px)',
              background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
              border: 'none',
              borderRadius: 12,
              color: '#ffffff',
              fontWeight: 800,
              fontSize: 'clamp(12px, 1.5vw, 14px)',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              animation: 'u_cta_glow 2.2s ease-in-out infinite',
              marginTop: 2,
              transition: 'transform 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'scale(1.04)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Enter Store →
          </button>
        </div>

        {/* Global Floating Skip Button: Available at any time */}
        <SkipButton onSkip={finish} accent={col.accent} />
      </div>
    </>
  );
};

export default IntroScreen;
